import type { WorkItemDto } from '@customTypes/types';
import { Role, WorkItemStatus, WorkItemTypes } from '@customTypes/types';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { Space } from '@mantine/core';
import { useEffect, useState } from 'react';

import { CreateConversationButton } from '@/features/chat/modules';
import { NotFoundWorkItem } from '@/features/work/components/not-found-work-item/NotFoundWorkItem';
import { PageLoading } from '@/pages/PageLoading';
import { usePageOffset } from '@/shared/hooks';
import { translate } from '@/shared/i18n/translate';
import { useProfileStore } from '@/shared/store/profileStore';
import { LBButton, LBFixedSection } from '@/shared/ui-kit/components';

import { useClubWorkItemsListQuery } from '../../work/api/club-work-items-list';
import { ArtistFullProfile } from '../../work/view/artist-full-profile/ArtistFullProfile';
import { interactionWorkItemsStatusFilters } from '../constants/filterStatuses';
import { ManageEmployerContainer } from '../modules/manage-employer/ManageEmployerContainer';
import { ManageWorkRequestContainer } from '../modules/manage-work-request/ManageWorkRequestContainer';
import { SendWorkInviteContaiener } from '../modules/send-work-invite-view/SendWorkInviteContainer';

export const ArtistInteractionContainer = ({
  artistId,
  workItemId,
  clubId
}: {
  artistId?: string;
  workItemId?: string;
  clubId: string;
}) => {
  const [currentView, setCurrentView] = useState<
    'send-invite' | 'manage-request' | 'manage-employer'
  >('send-invite');
  const { offset, ref } = usePageOffset();
  const { profile } = useProfileStore();
  const [currentWorkItem, setCurrentWorkItem] = useState<WorkItemDto | null>(
    null
  );
  const artistProfileId =
    artistId ?? currentWorkItem?.artistProfileId?.toString()!;

  const workItems = useClubWorkItemsListQuery({
    profileId: clubId,
    status: interactionWorkItemsStatusFilters
  });

  useEffect(() => {
    if (workItems.data?.data && artistId) {
      const item = workItems.data?.data.filter(
        (item) =>
          item.artistProfileId === +artistId &&
          (item.status === WorkItemStatus.Accepted ||
            item.status === WorkItemStatus.Pending ||
            item.status === WorkItemStatus.ClubSetEndDate ||
            item.status === WorkItemStatus.ContractRequestedEndDate ||
            item.status === WorkItemStatus.ContractOngoing)
      );
      if (item.length) {
        return setCurrentWorkItem(item[0]);
      }
      return setCurrentWorkItem(null);
    }

    if (workItems.data?.data && workItemId) {
      const item = workItems.data?.data.filter(
        (item) =>
          item.workItemId === +workItemId &&
          (item.status === WorkItemStatus.Accepted ||
            item.status === WorkItemStatus.Pending ||
            item.status === WorkItemStatus.ClubSetEndDate ||
            item.status === WorkItemStatus.ContractRequestedEndDate ||
            item.status === WorkItemStatus.ContractOngoing)
      );
      if (item.length) {
        return setCurrentWorkItem(item[0]);
      }
      return setCurrentWorkItem(null);
    }

    setCurrentWorkItem(null);
  }, [workItems.data?.data, artistId, workItemId]);

  useEffect(() => {
    if (
      currentWorkItem &&
      currentWorkItem.workItemType === WorkItemTypes.Invite &&
      currentWorkItem.status === WorkItemStatus.Pending
    ) {
      setCurrentView('send-invite');
    }

    if (
      currentWorkItem &&
      currentWorkItem.workItemType === WorkItemTypes.Request &&
      currentWorkItem?.status === WorkItemStatus.Pending
    ) {
      setCurrentView('manage-request');
    }

    if (
      currentWorkItem &&
      (currentWorkItem?.status === WorkItemStatus.Accepted ||
        currentWorkItem?.status === WorkItemStatus.ContractOngoing ||
        currentWorkItem?.status === WorkItemStatus.ContractRequestedEndDate ||
        currentWorkItem?.status === WorkItemStatus.ClubSetEndDate)
    ) {
      setCurrentView('manage-employer');
    }
  }, [currentWorkItem]);

  if (workItems.isLoading) return <PageLoading />;

  return (
    <div>
      {currentWorkItem || artistId ? (
        <div>
          {artistProfileId && (
            <>
              <ArtistFullProfile artistProfileId={artistProfileId} />
              <Space h={offset} />
            </>
          )}
          <LBFixedSection
            ref={ref}
            direction='column'
            gap={8}
            p={16}
            style={{
              backgroundColor: '#ffff',
              borderRadius: '8px',
              transform: 'translate(-50%)'
            }}
          >
            {currentView === 'send-invite' && (
              <SendWorkInviteContaiener
                artistId={artistProfileId}
                clubId={clubId}
                workItem={currentWorkItem}
              />
            )}
            {currentView === 'manage-request' && currentWorkItem && (
              <ManageWorkRequestContainer workItem={currentWorkItem} />
            )}
            {currentView === 'manage-employer' && currentWorkItem && (
              <ManageEmployerContainer workItem={currentWorkItem} />
            )}
            <CreateConversationButton
              recipientProfileId={artistProfileId}
              recipientProfileRole={Role.artist}
            >
              <LBButton
                fullWidth
                leftSection={<EnvelopeIcon width={18} />}
                variant='outline'
              >
                {profile?.role === Role.club
                  ? translate('commons.askArtist')
                  : translate('commons.askClub')}
              </LBButton>
            </CreateConversationButton>
          </LBFixedSection>
        </div>
      ) : (
        <NotFoundWorkItem />
      )}
    </div>
  );
};
